import React from 'react'
import { createRoot } from 'react-dom/client'
import { createInertiaApp } from '@inertiajs/react'
import { Toaster } from 'sonner'
import NProgress from 'nprogress'
import { LanguageProvider } from '../lib/LanguageContext'

import '../styles/application.css'

// ── Progress-bar suppression for SILENT, programmatic reloads ────────────────────────────────────
// Inertia's built-in progress plugin schedules NProgress.start() ~250 ms after EVERY visit begins.
// Some visits (e.g. PatientShow's live-refresh) only re-sync a few props and must NOT flash the top
// progress bar. We gate the shared NProgress singleton behind a depth counter: while suppressDepth
// is > 0, start() is a no-op, so no bar appears for that visit — and NO OTHER visit's progress
// behaviour changes (normal navigations still show the bar exactly as before). suppress()/release()
// are driven by a visit's onStart / onFinish, which Inertia ALWAYS fires as a pair, so the counter
// cannot leak. `NProgress` here is the same singleton @inertiajs/core drives, so patching start()
// once neutralises the built-in plugin's deferred start for suppressed visits only.
if (typeof window !== 'undefined' && NProgress && typeof NProgress.start === 'function') {
  const realStart = NProgress.start.bind(NProgress)
  let suppressDepth = 0
  NProgress.start = function () { return suppressDepth > 0 ? NProgress : realStart() }
  window.__inertiaProgress = {
    suppress() { suppressDepth += 1 },
    release() { suppressDepth = Math.max(0, suppressDepth - 1) },
  }
}

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    console.error('[AppErrorBoundary]', error, info)
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <p className="text-6xl font-bold text-red-500 mb-2">500</p>
          <h1 className="text-2xl font-semibold text-gray-900 mb-3">Something went wrong</h1>
          <p className="text-sm text-gray-500 mb-8">
            An unexpected error occurred in the application. Refreshing the page may resolve the issue.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <a
              href="/"
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Back to Dashboard
            </a>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    )
  }
}

createInertiaApp({
  resolve: (name) => {
    const pages = import.meta.glob('../pages/**/*.jsx', { eager: true })
    return pages[`../pages/${name}.jsx`]
  },
  setup({ el, App, props }) {
    createRoot(el).render(
      <AppErrorBoundary>
        <LanguageProvider initialServerLang={props.props?.ui_language}>
          <App {...props} />
          <Toaster position="top-right" richColors closeButton />
        </LanguageProvider>
      </AppErrorBoundary>
    )
  },
  progress: {
    color: '#4B5563',
  },
})
